import { translate as translateFunction, TranslationDefinition } from '@/core/localization/translate';
import { ManoExpressLargeLogo } from '@/core/tamagoshi/Logos/ManoExpressLargeLogo/ManoExpressLargeLogo';
import { Tag } from '@/domains/core/tamagoshi/Tag/Tag';
import { EcoLabel } from '@/domains/productDiscovery/Listings/components/EcoLabel/EcoLabel';
import { ColorGroup } from '@/domains/productDiscovery/Listings/components/Filters/components//MultiSelect/interfaces';
import { rangeFilter, rangeFilterMaxOnly, rangeFilterMinOnly } from '@/domains/productDiscovery/Listings/components/Filters/components/ActiveFilters/translations';
import { DotColor } from '@/domains/productDiscovery/Listings/components/Filters/components/MultiSelect/components/ColorSelectItem/components/DotColor';
import { colorTranslations } from '@/domains/productDiscovery/Listings/components/Filters/components/MultiSelect/components/ColorSelectItem/translations';
import { Ratings, type RatingsProps } from '@/domains/productDiscovery/Listings/components/Filters/components/RatingsFilter/components/Ratings/Ratings';
import { LocalLabel } from '@/domains/productDiscovery/Listings/components/LocalLabel/LocalLabel';
import { ProductTagsColor } from '@/domains/productDiscovery/Listings/components/ProductCardListingsEnhancer/components/ProductCardListings/components/ProductTags/ProductTags';
import { URL_MULTIPLE_FILTER_VALUE_SEPARATOR } from '@/domains/productDiscovery/Listings/constants';
import type { FacetRefinedFacet, RangeRefinedFacet, RefinedFacet } from '@/domains/productDiscovery/Listings/facets/hooks/useRefinedFacets';
import type { FilterConfig } from '@/domains/productDiscovery/Listings/interfaces/filterConfig';
import { translations } from '@/domains/productDiscovery/ProductCard/translations';
import styles from './ActiveFilter.module.scss';
export const generateLabelByFacetValue = (facetValue: RefinedFacet, filterConfig?: FilterConfig): string | JSX.Element => {
  switch (facetValue.type) {
    case 'boolean':
      return generateBooleanLabel(filterConfig);
    case 'checkbox':
    case 'color':
      return generateCheckboxLabel(facetValue);
    case 'custom':
      return generateCustomLabel(filterConfig);
    case 'range':
      return generateRangeLabel(facetValue, filterConfig);
    default:
      return facetValue.value;
  }
};
function generateBooleanLabel(filterConfig?: FilterConfig): string | JSX.Element {
  switch (filterConfig?.attributeName) {
    case 'is_mmf':
      return <ManoExpressLargeLogo className={styles.manoExpressLogo} />;
    case 'is_local':
      return <LocalLabel reversed />;
    case 'is_eco_responsible':
      return <EcoLabel reversed />;
    default:
      return filterConfig?.translations.label as string || 'unknown';
  }
}
function generateCheckboxLabel(facetValue: FacetRefinedFacet) {
  switch (facetValue.facetName) {
    case 'rating_rounded_down':
      return <Ratings value={parseInt(facetValue.value.split(URL_MULTIPLE_FILTER_VALUE_SEPARATOR)[0], 10) as RatingsProps['value']} />;
    case 'color':
      return <>
          <DotColor colorName={facetValue.value} styles={{
          margin: '0 4px 0 -8px'
        }} />
          {colorTranslations[facetValue.value as ColorGroup]}
        </>;
    default:
      return facetValue.value;
  }
}
function generateCustomLabel(filterConfig?: FilterConfig): string | JSX.Element {
  const {
    tags
  } = translations;
  if (filterConfig?.type === 'custom') {
    const tagName = filterConfig?.tagName as keyof typeof tags;
    return <>
        {filterConfig?.translations.label}
        {tagName && <Tag as="span" color={ProductTagsColor[tagName]} revamp>
            {tags[tagName]?.label}
          </Tag>}
      </>;
  }
  return filterConfig?.translations.label as string;
}
function generateRangeLabel(facetValue: RangeRefinedFacet, filterConfig?: FilterConfig): string {
  const [cleanedLabel, unit] = extractLabelAndUnit(filterConfig?.translations.label);
  let label!: string;
  if (facetValue.value.min && facetValue.value.max) {
    label = rangeFilter.format({
      label: cleanedLabel,
      min: facetValue.value.min,
      max: facetValue.value.max,
      unit
    }) as string;
  } else if (facetValue.value.min && !facetValue.value.max) {
    label = rangeFilterMinOnly.format({
      label: cleanedLabel,
      min: facetValue.value.min,
      unit
    }) as string;
  } else if (!facetValue.value.min && facetValue.value.max) {
    label = rangeFilterMaxOnly.format({
      label: cleanedLabel,
      max: facetValue.value.max,
      unit
    }) as string;
  }
  return label;
}
function extractLabelAndUnit(input?: string | TranslationDefinition): [string, string] {
  let processedInput: string | undefined;
  if (typeof input === 'string') {
    processedInput = input;
  } else if (input !== undefined) {
    processedInput = Object.values(translateFunction([input])).at(0);
  }
  let [unitWithParenthesis] = processedInput?.match(/\([^)]*\)/i) || [''];
  unitWithParenthesis = cleanUnit(unitWithParenthesis);
  return [processedInput?.replace(unitWithParenthesis, '') ?? '', unitWithParenthesis.replace('(', '').replace(')', '')];
}
function cleanUnit(input: string) {
  const openCount = (input.match(/\(/g) || []).length;
  const closeCount = (input.match(/\)/g) || []).length;
  if (openCount > closeCount) {
    return `${input}${new Array(openCount - closeCount).fill(')').join('')}`;
  }
  if (closeCount > openCount) {
    return `${new Array(closeCount - openCount).fill('(').join('')}${input}`;
  }
  return input;
}